import React, { useState } from 'react';
import Mustache from 'mustache';

const MultiEmailGenerator = () => {
  const [courseData, setCourseData] = useState({
    courseName: 'Python Fundamentals',
    startDate: '2024-03-15',
    endDate: '2024-05-15',
    instructorName: 'Dr. Smith',
    instructorEmail: 'dr.smith@university.edu',
    assignment1Date: '2024-03-22',
    assignment2Date: '2024-04-05',
    midtermDate: '2024-04-12',
    finalProjectDate: '2024-05-10',
    week1Date: '2024-03-15',
    week2Date: '2024-03-22',
    week3Date: '2024-03-29',
    week4Date: '2024-04-05'
  });

  // Template library - this would typically be loaded from a file or database
  const [templates] = useState([
    {
      id: 'welcome',
      name: 'Welcome Email',
      category: 'Onboarding',
      description: 'Initial welcome message',
      template: `<html><body><h1>Welcome to {{courseName}}!</h1><p>Course starts {{startDate}} with {{instructorName}}</p></body></html>`
    },
    {
      id: 'week1-reminder',
      name: 'Week 1 Reminder',
      category: 'Weekly',
      description: 'First week preparation',
      template: `<html><body><h1>{{courseName}} - Week 1 Starts {{week1Date}}</h1><p>Get ready for your first week with {{instructorName}}!</p></body></html>`
    },
    {
      id: 'assignment1-reminder',
      name: 'Assignment 1 Reminder',
      category: 'Assignments',
      description: 'First assignment due reminder',
      template: `<html><body><h1>Assignment 1 Due Soon</h1><p>Don't forget: Assignment 1 for {{courseName}} is due {{assignment1Date}}</p></body></html>`
    },
    {
      id: 'midterm-prep',
      name: 'Midterm Preparation',
      category: 'Exams',
      description: 'Midterm exam preparation',
      template: `<html><body><h1>Midterm Preparation - {{courseName}}</h1><p>Your midterm exam is scheduled for {{midtermDate}}. Contact {{instructorName}} at {{instructorEmail}} with questions.</p></body></html>`
    },
    {
      id: 'final-project',
      name: 'Final Project Guidelines',
      category: 'Projects',
      description: 'Final project information',
      template: `<html><body><h1>Final Project - {{courseName}}</h1><p>Final project due: {{finalProjectDate}}. Instructor: {{instructorName}}</p></body></html>`
    },
    // Add more templates here - this shows the pattern for 28 emails
    ...Array.from({length: 23}, (_, i) => ({
      id: `template-${i + 6}`,
      name: `Email Template ${i + 6}`,
      category: i < 8 ? 'Weekly' : i < 16 ? 'Assignments' : 'Announcements',
      description: `Template ${i + 6} description`,
      template: `<html><body><h1>{{courseName}} - Template ${i + 6}</h1><p>This is template ${i + 6} for {{instructorName}}'s course.</p></body></html>`
    }))
  ]);

  const [selectedTemplate, setSelectedTemplate] = useState(templates[0]);
  const [editingTemplate, setEditingTemplate] = useState(templates[0].template);
  const [generatedEmails, setGeneratedEmails] = useState({});
  const [activeTab, setActiveTab] = useState('single');

  const categories = [...new Set(templates.map(t => t.category))];

  const handleDataChange = (field, value) => {
    setCourseData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateSingleEmail = () => {
    const rendered = Mustache.render(editingTemplate, courseData);
    setGeneratedEmails(prev => ({
      ...prev,
      [selectedTemplate.id]: {
        ...selectedTemplate,
        html: rendered
      }
    }));
  };

  const generateAllEmails = () => {
    const generated = {};
    templates.forEach(template => {
      const rendered = Mustache.render(template.template, courseData);
      generated[template.id] = {
        ...template,
        html: rendered
      };
    });
    setGeneratedEmails(generated);
  };

  const downloadEmail = (emailId) => {
    const email = generatedEmails[emailId];
    if (!email) return;
    
    const blob = new Blob([email.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${email.id}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllEmails = () => {
    // Create a simple zip-like approach by downloading multiple files
    Object.keys(generatedEmails).forEach((emailId, index) => {
      setTimeout(() => downloadEmail(emailId), index * 200);
    });
  };

  const copyEmailHtml = (emailId) => {
    const email = generatedEmails[emailId];
    if (email) {
      navigator.clipboard.writeText(email.html);
      alert(`${email.name} HTML copied to clipboard!`);
    }
  };

  const templatesByCategory = (category) => 
    templates.filter(t => t.category === category);

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-center mb-8 text-gray-800">
        Course Email Campaign Generator
      </h1>
      
      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-200 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('single')}
            className={`px-4 py-2 rounded-md transition-colors ${
              activeTab === 'single' 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Single Email Editor
          </button>
          <button
            onClick={() => setActiveTab('batch')}
            className={`px-4 py-2 rounded-md transition-colors ${
              activeTab === 'batch' 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Batch Generation
          </button>
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-md transition-colors ${
              activeTab === 'overview' 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Campaign Overview
          </button>
        </div>
      </div>

      {/* Course Data Panel - Always Visible */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-700">Course Details</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Course Name</label>
            <input
              type="text"
              value={courseData.courseName}
              onChange={(e) => handleDataChange('courseName', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Instructor</label>
            <input
              type="text"
              value={courseData.instructorName}
              onChange={(e) => handleDataChange('instructorName', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Start Date</label>
            <input
              type="date"
              value={courseData.startDate}
              onChange={(e) => handleDataChange('startDate', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">End Date</label>
            <input
              type="date"
              value={courseData.endDate}
              onChange={(e) => handleDataChange('endDate', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Single Email Editor Tab */}
      {activeTab === 'single' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Template Selector */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">Select Template</h2>
            {categories.map(category => (
              <div key={category} className="mb-4">
                <h3 className="font-medium text-gray-600 mb-2">{category}</h3>
                <div className="space-y-1">
                  {templatesByCategory(category).map(template => (
                    <button
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setEditingTemplate(template.template);
                      }}
                      className={`w-full text-left p-2 rounded text-sm transition-colors ${
                        selectedTemplate.id === template.id
                          ? 'bg-blue-100 text-blue-800'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Template Editor */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">
              Edit: {selectedTemplate.name}
            </h2>
            <textarea
              value={editingTemplate}
              onChange={(e) => setEditingTemplate(e.target.value)}
              className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm"
            />
            <button
              onClick={generateSingleEmail}
              className="w-full mt-4 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
            >
              Generate This Email
            </button>
          </div>

          {/* Preview */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold mb-4 text-gray-700">Preview</h2>
            {generatedEmails[selectedTemplate.id] && (
              <div>
                <div 
                  className="border rounded p-3 bg-white max-h-64 overflow-auto mb-4"
                  dangerouslySetInnerHTML={{ 
                    __html: generatedEmails[selectedTemplate.id].html 
                  }}
                />
                <div className="space-x-2">
                  <button
                    onClick={() => copyEmailHtml(selectedTemplate.id)}
                    className="bg-green-600 text-white py-1 px-3 rounded text-sm hover:bg-green-700"
                  >
                    Copy HTML
                  </button>
                  <button
                    onClick={() => downloadEmail(selectedTemplate.id)}
                    className="bg-purple-600 text-white py-1 px-3 rounded text-sm hover:bg-purple-700"
                  >
                    Download
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Batch Generation Tab */}
      {activeTab === 'batch' && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-700">Batch Generate All Emails</h2>
            <div className="space-x-3">
              <button
                onClick={generateAllEmails}
                className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700"
              >
                Generate All {templates.length} Emails
              </button>
              <button
                onClick={downloadAllEmails}
                className="bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700"
                disabled={Object.keys(generatedEmails).length === 0}
              >
                Download All
              </button>
            </div>
          </div>
          
          <div className="text-center text-gray-600 mb-4">
            Generated: {Object.keys(generatedEmails).length} / {templates.length} emails
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(template => (
              <div key={template.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-medium">{template.name}</h3>
                    <p className="text-sm text-gray-600">{template.category}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    generatedEmails[template.id] 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {generatedEmails[template.id] ? 'Generated' : 'Pending'}
                  </span>
                </div>
                {generatedEmails[template.id] && (
                  <div className="space-x-2 mt-3">
                    <button
                      onClick={() => copyEmailHtml(template.id)}
                      className="text-xs bg-gray-100 hover:bg-gray-200 py-1 px-2 rounded"
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => downloadEmail(template.id)}
                      className="text-xs bg-gray-100 hover:bg-gray-200 py-1 px-2 rounded"
                    >
                      Download
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Campaign Overview Tab */}
      {activeTab === 'overview' && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-6 text-gray-700">Campaign Overview</h2>
          
          {categories.map(category => (
            <div key={category} className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">{category}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templatesByCategory(category).map(template => (
                  <div key={template.id} className="border rounded p-3">
                    <h4 className="font-medium">{template.name}</h4>
                    <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                      generatedEmails[template.id] 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {generatedEmails[template.id] ? 'Ready' : 'Not Generated'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiEmailGenerator;